import type {CSSProperties, ReactElement, RefObject} from 'react';
import {useEffect, useRef, useState} from 'react';
import type {MediaTypePublishingParams, SocialMediaAccountId} from '@Components/social-media/post.vo';
import {PlatformMediaType} from '@Components/social-media/post.vo';
import type {PlatformPostPreviewProps} from './social-post-content-multiposting.types';
import {
  DEFAULT_PREVIEW_WIDTH_INSET_PX,
  getPreviewKind,
  getPreviewStyleConfig,
  ORPHANED_PREVIEW_HEIGHT_PX,
  ORPHANED_PREVIEW_WIDTH_PX,
  PreviewKind,
  SocialPostContentType,
} from './social-post-content-multiposting.types';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {isThreadsAccount, isTwitterAccount} from '@Components/social-media/account.vo';
import {CircularProgressLoader, LOADER_SIZE} from '@Components/circular-progress-loader';
import {SocialPostPreviewSkeleton} from '@Components/social-media/components/social-post-preview-skeleton/social-post-preview-skeleton';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {SocialPostContentMediaWrapper} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-media-wrapper';
import type {PreviewMediaOptions} from './social-post-content-media-wrapper/social-post-content-media-wrapper.types';
import {getFeedMediaLayout, MAX_SIDE_BY_SIDE_CELLS, PreviewMediaBackdrop, PreviewMediaFit} from './social-post-content-media-wrapper/social-post-content-media-wrapper.types';
import type {DesignSelection} from '@Components/social-media/social-media.types';
import {SocialPlatform} from '@Components/social-media/social-media.types';
import {Text} from '@Components/text';
import type {GraphicsMetaMapType, PageHashedId, PosterHashedId} from '@Components/social-media/publish-params.types';
import {Icon} from '@Components/icon-v2';
import {FlatIconColor, FlatIconColorType, IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {noop} from '@Utils/general.util';
import {assertNever} from '@Utils/assert-never.util';
import {getAllDesignsFromGraphicsMetaMap, getRenderableDesignFromGraphicsMetaMap, isDesignInGraphicsMetaMap} from '@Libraries/social-media-params.library';
import {FacebookPostPreview} from './components/facebook-post-preview/facebook-post-preview';
import {InstagramPostPreview} from './components/instagram-post-preview/instagram-post-preview';
import {LinkedinPostPreview} from './components/linkedin-post-preview/linkedin-post-preview';
import {TwitterPostPreview} from './components/twitter-post-preview/twitter-post-preview';
import {ThreadsPostPreview} from './components/threads-post-preview/threads-post-preview';
import {PinterestPostPreview} from './components/pinterest-post-preview/pinterest-post-preview';
import {GoogleBusinessPostPreview} from './components/google-business-post-preview/google-business-post-preview';
import {StoryPreview} from './components/story-preview/story-preview';
import {VerticalVideoPreview} from './components/vertical-video-preview/vertical-video-preview';
import {useGraphicsSignature, useResetLoadingOnGraphicsChange} from './social-post-content-multiposting.hooks';
import styles from './social-post-content-multiposting.module.scss';

export interface SocialPostContentProps {
  type: SocialPostContentType;
  account?: SocialAccount;
  parentContainerRef?: RefObject<HTMLDivElement | null>;
  areDetailsLoading: boolean;
  onLoadCallback?: () => void;
  designsToPublish: DesignSelection[];
  accountsToPublishTo: SocialAccount[];
  publishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>;
  platformMediaType?: PlatformMediaType;
  /** When omitted, the orphaned preview renders as a plain, non-clickable surface. */
  onOrphanedPreviewContainerClick?: VoidFunction;
  /** Horizontal room kept around the card inside the parent; the card scales down only when the parent is narrower than card + inset. */
  previewWidthInsetPx?: number;
  designsGraphicMetaMap: GraphicsMetaMapType;
  /** Extra classes for the carousel arrow strip, merged on top of the per-platform preview's own. */
  carouselButtonContainerClassName?: string;
  /** Forwarded to the carousel: touch surfaces have no hover, so the arrows are dropped entirely without this. */
  areCarouselArrowsAlwaysVisible?: boolean;
  /** Opt in to the full post-card skeleton while media loads, in place of the centred spinner. */
  hasSkeletonLoadingState?: boolean;
}

export function SocialPostContent({
  type,
  account,
  parentContainerRef,
  areDetailsLoading,
  onLoadCallback = noop,
  designsToPublish,
  accountsToPublishTo,
  publishingParams,
  onOrphanedPreviewContainerClick,
  platformMediaType = PlatformMediaType.POST,
  previewWidthInsetPx = DEFAULT_PREVIEW_WIDTH_INSET_PX,
  designsGraphicMetaMap,
  carouselButtonContainerClassName,
  areCarouselArrowsAlwaysVisible,
  hasSkeletonLoadingState = false,
}: SocialPostContentProps): ReactElement {
  const [isMediaLoading, setIsMediaLoading] = useState(true);
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const previewWrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);
  const [graphicItemLoadingMap, setGraphicItemLoadingMap] = useState<Record<PosterHashedId, Record<PageHashedId, boolean>>>({});

  const graphicsSignature = useGraphicsSignature(designsGraphicMetaMap);

  const resetGraphicsLoadingState = (): void => {
    setIsMediaLoading(true);
    setGraphicItemLoadingMap(getInitialPostersLoadingMap());
  };

  useResetLoadingOnGraphicsChange({accountId: account?.id, platformMediaType: platformMediaType, graphicsSignature: graphicsSignature}, resetGraphicsLoadingState);

  const previewKind = getPreviewKind(account, platformMediaType);

  const isVerticalPreview = (): boolean => {
    return previewKind === PreviewKind.VERTICAL_VIDEO;
  };

  const doesSomeAccountExist = (): boolean => {
    return account !== undefined || accountsToPublishTo.length > 0;
  };

  const getTotalNumberOfPages = (): number => {
    return Object.values(designsGraphicMetaMap).reduce((total, pageGraphicsMetaMap) => total + Object.keys(pageGraphicsMetaMap).length, 0);
  };

  const getNumberOfAssetsToShow = (): number => {
    if (isVerticalPreview()) {
      return getDesignsForMedia().length;
    }

    if (doesSomeAccountExist() && previewKind !== PreviewKind.FEED_POST) {
      return 1;
    }

    if (isThreadsAccount(account) || isTwitterAccount(account)) {
      return getTotalNumberOfPages();
    }

    return Math.min(getTotalNumberOfPages(), MAX_SIDE_BY_SIDE_CELLS);
  };

  const getInitialPostersLoadingMap = (): Record<PosterHashedId, Record<PageHashedId, boolean>> => {
    const initialPosterLoadingMap: Record<PosterHashedId, Record<PageHashedId, boolean>> = {};
    const numberOfAssetsToShow = getNumberOfAssetsToShow();

    let count = 0;

    Object.keys(designsGraphicMetaMap).forEach((posterHashId) => {
      Object.keys(designsGraphicMetaMap[posterHashId]).forEach((pageHashedId) => {
        if (count < numberOfAssetsToShow) {
          initialPosterLoadingMap[posterHashId] ??= {};
          initialPosterLoadingMap[posterHashId][pageHashedId] = false;
          count += 1;
        }
      });
    });

    return initialPosterLoadingMap;
  };

  useEffect((): void => {
    const initialLoadingMap = getInitialPostersLoadingMap();
    setGraphicItemLoadingMap(initialLoadingMap);
  }, []);

  useEffect((): void => {
    if (!isMediaLoading) {
      onLoadCallback();
    }
  }, [isMediaLoading]);

  useEffect((): void => {
    if (contentContainerRef?.current) {
      const height = contentContainerRef.current.clientHeight;
      const width = contentContainerRef.current.clientWidth;

      setContainerDimensions(height, width);

      if (!isMediaLoading || type === SocialPostContentType.MEDIA_RESIZING_FAILURE) {
        contentContainerRef.current.classList.add(styles.loaded);
      } else {
        contentContainerRef.current.classList.remove(styles.loaded);
      }
    }
  }, [contentContainerRef?.current, isMediaLoading, type, publishingParams, scale]);

  const getAvailablePreviewWidth = (): number => {
    const parentWidth = parentContainerRef?.current?.clientWidth ?? 0;
    return parentWidth - previewWidthInsetPx;
  };

  /** The card renders at its intrinsic design size and only shrinks when the parent cannot fit it. */
  const getScaleForDesignWidth = (designWidthPx: number): number => {
    return Math.min(1, Math.max(0, getAvailablePreviewWidth() / designWidthPx));
  };

  const getScaleForAccount = (currentAccount: SocialAccount): number => {
    const {width} = getPreviewStyleConfig(currentAccount.type, isVerticalPreview(), platformMediaType);
    return getScaleForDesignWidth(parseInt(width, 10));
  };

  const updateScaleFromParentWidth = (): void => {
    if (!parentContainerRef?.current) {
      return;
    }

    if (doesSomeAccountExist()) {
      setScale(getScaleForAccount(account ?? accountsToPublishTo[0]));
      return;
    }

    setScale(getScaleForDesignWidth(ORPHANED_PREVIEW_WIDTH_PX));
  };

  useEffect((): VoidFunction => {
    updateScaleFromParentWidth();

    const parentContainer = parentContainerRef?.current;

    if (!parentContainer) {
      return noop;
    }

    const parentResizeObserver = new ResizeObserver((): void => {
      updateScaleFromParentWidth();
    });

    parentResizeObserver.observe(parentContainer);

    return (): void => {
      parentResizeObserver.disconnect();
    };
  }, [parentContainerRef, type, account, platformMediaType]);

  const setContainerDimensions = (height: number, width: number): void => {
    if (containerRef?.current) {
      containerRef.current.style.height = `${height * scale}px`;
      containerRef.current.style.width = `${width * scale}px`;
    }
  };

  const doUpdateMediaLoadingIfNeeded = (updatedPostersLoadingState: Record<PosterHashedId, Record<PageHashedId, boolean>>): void => {
    const allPreviewsLoaded = Object.values(updatedPostersLoadingState).every((pageLoadingState) => {
      return Object.values(pageLoadingState).every((pageLoadingState) => pageLoadingState);
    });

    if (allPreviewsLoaded) {
      setIsMediaLoading(false);
    }
  };

  const onGraphicLoad = ({posterHashedId, pageHashedId}: DesignSelection): void => {
    setGraphicItemLoadingMap((prevState) => {
      const updatedState = {
        ...prevState,
        [posterHashedId]: {
          ...prevState[posterHashedId],
          [pageHashedId]: true,
        },
      };

      doUpdateMediaLoadingIfNeeded(updatedState);
      return updatedState;
    });
  };

  const getEffectiveDesigns = (): DesignSelection[] => {
    if (designsToPublish.length > 0) {
      return designsToPublish;
    }

    return getAllDesignsFromGraphicsMetaMap(designsGraphicMetaMap);
  };

  const getVerticalPreviewDesign = (): DesignSelection | null => {
    return getRenderableDesignFromGraphicsMetaMap(designsToPublish, designsGraphicMetaMap);
  };

  const getVerticalPreviewDesigns = (): DesignSelection[] => {
    const renderableSelectedDesigns = getEffectiveDesigns().filter((design) => isDesignInGraphicsMetaMap(design, designsGraphicMetaMap));

    if (renderableSelectedDesigns.length > 0) {
      return renderableSelectedDesigns;
    }

    const fallbackDesign = getVerticalPreviewDesign();
    return fallbackDesign === null ? [] : [fallbackDesign];
  };

  const getDesignsForMedia = (): DesignSelection[] => {
    if (isVerticalPreview()) {
      return getVerticalPreviewDesigns();
    }

    return getEffectiveDesigns();
  };

  const getOrphanedPreviewCardStyle = (): CSSProperties => {
    return {width: `${ORPHANED_PREVIEW_WIDTH_PX}px`, height: `${ORPHANED_PREVIEW_HEIGHT_PX}px`, scale: `${scale}`};
  };

  const orphanedPreview = (): ReactElement | null => {
    return (
      <div ref={contentContainerRef} style={getOrphanedPreviewCardStyle()} className={`${styles.contentBackground} ${styles.orphanedPreviewCard} spacing-p-5`}>
        <div className={`${styles.orphanedPreviewBody} _full-width`}>
          <div className={styles.orphanedPreviewSkeletonGroup}>
            <div className={styles.orphanedPreviewHeader}>
              <SkeletonBrick isPulsating={isMediaLoading} className={styles.orphanedPreviewSkeletonPill} width={32} height={32} />
              <SkeletonBrick isPulsating={isMediaLoading} className={styles.orphanedPreviewSkeletonPill} width={240} height={16} />
            </div>
            <SkeletonBrick isPulsating={isMediaLoading} className={styles.orphanedPreviewSkeletonPill} height={13} />
            <SkeletonBrick isPulsating={isMediaLoading} className={styles.orphanedPreviewSkeletonPill} width={147} height={13} />
          </div>
          <SocialPostContentMediaWrapper
            graphicsLoadingMap={graphicItemLoadingMap}
            designsGraphicsMetaMap={designsGraphicMetaMap}
            designs={getEffectiveDesigns()}
            onAssetLoad={onGraphicLoad}
            onOrphanedPreviewContainerClick={onOrphanedPreviewContainerClick}
            doRetryOnError
            layout={getFeedMediaLayout(getEffectiveDesigns().length)}
            backdrop={PreviewMediaBackdrop.LIGHT}
            fit={PreviewMediaFit.COVER}
            frameClassName={styles.orphanedPreviewMediaFrame}
          />
        </div>
      </div>
    );
  };

  const getResizeFailureContent = (): ReactElement => {
    return (
      <div className={styles.resizeFailureContainer}>
        <Icon
          flatIconType={{color: FlatIconColor.DANGER, type: FlatIconColorType.DARK}}
          type={IconType.FLAT}
          shape={IconShape.CIRCLE}
          size={IconSize.SIZE_ICON_20}
          className={`${styles.errorIcon} spacing-m-b-2`}
          icon="icon-exclamation-triangle"
        />
        <Text className={`content-danger ${styles.errorText}`} val={window.i18next.t('pmwjs_resizing_design_error')} />
      </div>
    );
  };

  const getCarouselButtonContainerClasses = (platformCarouselButtonContainerClassName: string | undefined): string => {
    return `${platformCarouselButtonContainerClassName ?? ''} ${carouselButtonContainerClassName ?? ''}`.trim();
  };

  const renderMedia = (options: PreviewMediaOptions): ReactElement => {
    if (type === SocialPostContentType.MEDIA_RESIZING_FAILURE) {
      return getResizeFailureContent();
    }

    return (
      <SocialPostContentMediaWrapper
        graphicsLoadingMap={graphicItemLoadingMap}
        designsGraphicsMetaMap={designsGraphicMetaMap}
        designs={getDesignsForMedia()}
        onAssetLoad={onGraphicLoad}
        doRetryOnError
        {...options}
        carouselButtonContainerClassName={getCarouselButtonContainerClasses(options.carouselButtonContainerClassName)}
        areCarouselArrowsAlwaysVisible={options.areCarouselArrowsAlwaysVisible ?? areCarouselArrowsAlwaysVisible}
      />
    );
  };

  const getContentBackgroundStyle = (currentAccount: SocialAccount): CSSProperties => {
    const {width, height} = getPreviewStyleConfig(currentAccount.type, isVerticalPreview(), platformMediaType);

    if (previewKind !== PreviewKind.FEED_POST) {
      return {width, height, scale};
    }

    return {width, minHeight: height, scale};
  };

  const getPostPreviewForPlatform = (previewProps: PlatformPostPreviewProps): ReactElement => {
    switch (previewProps.account.type) {
      case SocialPlatform.FACEBOOK:
      case SocialPlatform.FACEBOOK_PAGE:
      case SocialPlatform.FACEBOOK_PROFILE:
      case SocialPlatform.FACEBOOK_GROUP:
        return <FacebookPostPreview {...previewProps} />;
      case SocialPlatform.INSTAGRAM:
        return <InstagramPostPreview {...previewProps} />;
      case SocialPlatform.LINKEDIN:
      case SocialPlatform.LINKEDIN_PAGE:
      case SocialPlatform.LINKEDIN_PROFILE:
        return <LinkedinPostPreview {...previewProps} />;
      case SocialPlatform.TWITTER:
        return <TwitterPostPreview {...previewProps} />;
      case SocialPlatform.THREADS:
        return <ThreadsPostPreview {...previewProps} />;
      case SocialPlatform.PINTEREST:
        return <PinterestPostPreview {...previewProps} />;
      case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
        return <GoogleBusinessPostPreview {...previewProps} />;
      case SocialPlatform.TIKTOK:
      case SocialPlatform.YOUTUBE:
        return <VerticalVideoPreview {...previewProps} />;
      default:
        return assertNever(previewProps.account.type);
    }
  };

  const getPlatformPreview = (previewProps: PlatformPostPreviewProps): ReactElement => {
    if (type === SocialPostContentType.MEDIA_RESIZING_FAILURE) {
      return getPostPreviewForPlatform(previewProps);
    }

    switch (previewKind) {
      case PreviewKind.VERTICAL_VIDEO:
        return <VerticalVideoPreview {...previewProps} />;
      case PreviewKind.STORY:
        return <StoryPreview {...previewProps} />;
      case PreviewKind.FEED_POST:
        return getPostPreviewForPlatform(previewProps);
      default:
        return assertNever(previewKind);
    }
  };

  const isVerticalPreviewWithoutRenderableDesign = (): boolean => {
    return isVerticalPreview() && type !== SocialPostContentType.MEDIA_RESIZING_FAILURE && getVerticalPreviewDesign() === null;
  };

  const getAccountPreview = (): ReactElement | null => {
    if (!account || areDetailsLoading || isVerticalPreviewWithoutRenderableDesign()) {
      return orphanedPreview();
    }

    const previewProps: PlatformPostPreviewProps = {
      account,
      platformMediaType,
      publishingParams,
      numberOfPages: getDesignsForMedia().length,
      renderMedia,
    };

    return (
      <div ref={contentContainerRef} style={getContentBackgroundStyle(account)} className={styles.contentBackground} key={account.id}>
        {getPlatformPreview(previewProps)}
      </div>
    );
  };

  const isOrphanedPreview = (): boolean => {
    if (accountsToPublishTo.length === 0 && !account) {
      return true;
    }

    return type !== SocialPostContentType.MEDIA_RESIZING && type !== SocialPostContentType.MEDIA_RESIZING_FAILURE && type !== SocialPostContentType.ACCOUNT_PREVIEW;
  };

  const getPreview = (): ReactElement | null => {
    if (isOrphanedPreview()) {
      return orphanedPreview();
    }

    if (type === SocialPostContentType.MEDIA_RESIZING) {
      return null;
    }

    return getAccountPreview();
  };

  const getSkeletonLoadingState = (): ReactElement | null => {
    if (!isMediaLoading) {
      return null;
    }

    return <SocialPostPreviewSkeleton cardStyle={isOrphanedPreview() ? getOrphanedPreviewCardStyle() : undefined} />;
  };

  const getLoadingIndicator = (): ReactElement | null => {
    if (hasSkeletonLoadingState) {
      return getSkeletonLoadingState();
    }

    return <CircularProgressLoader classes={isMediaLoading && type !== SocialPostContentType.MEDIA_RESIZING_FAILURE ? styles.loaderClass : '_hidden'} size={LOADER_SIZE.MEDIUM} />;
  };

  return (
    <div className="_full-height">
      {getLoadingIndicator()}
      <div ref={previewWrapperRef} className={styles.previewWrapper}>
        <div ref={containerRef} className={styles.previewContent}>
          {getPreview()}
        </div>
      </div>
    </div>
  );
}
